import React, { useRef, useState, useEffect, useCallback } from 'react';
import RPPError from '../../utils/error';
import { createRequestNumberGenerator } from '../../utils/requestNumberGenerator';
import { useThrottledRasterUrl } from '../../hooks/useThrottledRasterUrl';
import { loadImageFromBlob } from '../../utils/loadImage';
import { releaseCanvases } from '../../utils/releaseCanvases';
export function RasterPreview(_ref) {
  let {
    children,
    view,
    renderOptions,
    cimDocUrl,
    throttleWaitMs,
    onError,
    onPaint
  } = _ref;
  const canvasRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState();
  const requestNumberGenerator = useRef(createRequestNumberGenerator());
  const throttledRasterUrl = useThrottledRasterUrl({
    view,
    renderOptions,
    containerWidth,
    cimDocUrl,
    throttleWaitMs
  });
  const [status, setStatus] = useState({
    loading: false
  });
  const setRef = newRef => {
    if (newRef === null && canvasRef.current) {
      releaseCanvases([canvasRef.current]);
    }
    canvasRef.current = newRef;
  };
  useEffect(() => {
    var _a;
    if (!canvasRef.current || !((_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.parentElement)) {
      return;
    }
    const resizeObserver = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    resizeObserver.observe(canvasRef.current.parentElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!throttledRasterUrl || !canvasRef.current) {
      return;
    }
    const currentRequestTracker = requestNumberGenerator.current.next();
    const isRequestStillRelevant = () => requestNumberGenerator.current.lastCompletedRequest() < currentRequestTracker;
    setStatus({
      loading: true,
      error: undefined
    });
    const drawResponse = async response => {
      if (response.ok) {
        if (!isRequestStillRelevant()) {
          return;
        }
        const blob = await response.clone().blob();
        if (!isRequestStillRelevant()) {
          return;
        }
        const {
          image,
          width,
          height
        } = await loadImageFromBlob(blob);
        if (!isRequestStillRelevant()) {
          return;
        }
        requestNumberGenerator.current.setLastCompletedRequest(currentRequestTracker);
        if (canvasRef.current) {
          canvasRef.current.width = width;
          canvasRef.current.height = height;
          const context = canvasRef.current.getContext('2d');
          context.drawImage(image, 0, 0, width, height);
          onPaint === null || onPaint === void 0 ? void 0 : onPaint();
        }
        setStatus({
          loading: false,
          error: undefined
        });
      } else {
        if (!isRequestStillRelevant()) {
          return;
        }
        const error = new RPPError("Error rendering URL ".concat(response.url), response.status);
        onError === null || onError === void 0 ? void 0 : onError(error);
        setStatus({
          loading: false,
          error
        });
      }
    };
    const handleError = error => {
      if (!isRequestStillRelevant()) {
        return;
      }
      onError === null || onError === void 0 ? void 0 : onError(error);
      setStatus({
        loading: false,
        error
      });
    };
    fetch(throttledRasterUrl).then(drawResponse).catch(handleError);
  }, [throttledRasterUrl, setStatus, requestNumberGenerator, canvasRef, onError, onPaint]);
  const Canvas = useCallback(props => React.createElement("canvas", Object.assign({
    ref: setRef,
    style: {
      width: '100%'
    }
  }, props)), []);
  if (!children) {
    return React.createElement(Canvas, null);
  }
  return React.createElement(React.Fragment, null, children({
    Canvas,
    status
  }));
}